import { NavLink, useNavigate } from "react-router-dom";
import { Home, PlusCircle, BarChart2, History, User, LogOut, LayoutDashboard, MapPin, Tag, Users, Target, FileText, Building2, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

export function MemberLayout({ children, title }) {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const items = [
    { to: "/hem", label: "Hem", icon: Home, tid: "nav-home" },
    { to: "/registrera", label: "Registrera", icon: PlusCircle, tid: "nav-register" },
    { to: "/statistik", label: "Statistik", icon: BarChart2, tid: "nav-stats" },
    { to: "/historik", label: "Historik", icon: History, tid: "nav-history" },
    { to: "/profil", label: "Profil", icon: User, tid: "nav-profile" },
  ];
  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo variant="inline" size="md"/>
          {(user?.role === "central_admin" || user?.role === "local_admin") && (
            <button onClick={() => nav("/admin")} data-testid="nav-admin-switch"
              className="text-xs font-semibold text-[#E1251B] hover:underline">Admin →</button>
          )}
        </div>
        {title && <div className="max-w-2xl mx-auto px-5 pb-4"><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1></div>}
      </header>
      <main className="max-w-2xl mx-auto px-5 py-6">{children}</main>
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200">
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          {items.map(({ to, label, icon: Icon, tid }) => (
            <NavLink key={to} to={to} data-testid={tid}
              className={({ isActive }) => `flex flex-col items-center justify-center py-3 gap-1 text-[11px] font-semibold ${isActive ? "text-[#E1251B]" : "text-slate-500 hover:text-slate-900"}`}>
              <Icon size={20} strokeWidth={2} />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function AdminLayout({ children, title, subtitle, actions }) {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const items = [
    { to: "/admin", label: "Översikt", icon: LayoutDashboard, roles: ["central_admin","local_admin"], tid: "adm-nav-dash" },
    { to: "/admin/arbetarekommuner", label: "Arbetarekommuner", icon: Building2, roles: ["central_admin"], tid: "adm-nav-orgs" },
    { to: "/admin/platser", label: "Platser", icon: MapPin, roles: ["central_admin","local_admin"], tid: "adm-nav-locs" },
    { to: "/admin/aktivitetstyper", label: "Aktivitetstyper", icon: Tag, roles: ["central_admin"], tid: "adm-nav-at" },
    { to: "/admin/medlemmar", label: "Medlemmar", icon: Users, roles: ["central_admin","local_admin"], tid: "adm-nav-users" },
    { to: "/admin/mal", label: "Kampanjmål", icon: Target, roles: ["central_admin"], tid: "adm-nav-targets" },
    { to: "/admin/rapporter", label: "Rapporter", icon: FileText, roles: ["central_admin","local_admin"], tid: "adm-nav-reports" },
    { to: "/admin/veckorapport", label: "Veckorapport", icon: Sparkles, roles: ["central_admin","local_admin"], tid: "adm-nav-weekly" },
  ].filter(i => i.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <aside className="hidden lg:flex flex-col w-64 min-h-screen bg-white border-r border-slate-200 p-6 sticky top-0">
          <div className="mb-8">
            <Logo variant="stacked" size="lg" withSubtitle subtitle="Snacket"/>
          </div>
          <nav className="flex flex-col gap-1">
            {items.map(({ to, label, icon: Icon, tid }) => (
              <NavLink key={to} to={to} end data-testid={tid}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <Icon size={17} strokeWidth={2} />{label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto pt-6 border-t border-slate-200 flex flex-col gap-2">
            <button onClick={() => nav("/hem")} data-testid="admin-nav-member" className="text-xs text-slate-500 hover:text-slate-900 text-left">← Medlemsvy</button>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900"><LogOut size={14}/>Logga ut</button>
          </div>
        </aside>
        <main className="flex-1 min-w-0">
          <header className="bg-white border-b border-slate-200 px-6 lg:px-10 py-6 sticky top-0 z-20">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
                {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
              </div>
              <div className="flex gap-2 items-center">{actions}</div>
            </div>
          </header>
          <div className="p-6 lg:p-10">{children}</div>
        </main>
      </div>
    </div>
  );
}
